/**
 * /admin/email-test — owner tool for verifying email deliverability.
 *
 * Fires a real transactional email to any address via Resend so you
 * can confirm Gmail / Outlook / iCloud are all delivering to inbox
 * (not spam) before launch.
 *
 * Also includes the DNS-record checklist as a reference panel.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { Mail, Send, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PageTitle from '../components/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrAbove } from '../lib/tier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_CHECKS = [
  { name: 'Gmail',    placeholder: 'name@gmail.com',    hint: 'Spam filter: strict. Inbox = strong sign.' },
  { name: 'Outlook',  placeholder: 'name@outlook.com',  hint: 'Spam filter: very strict. Often the hardest to land in.' },
  { name: 'iCloud',   placeholder: 'name@icloud.com',   hint: 'Spam filter: moderate. Watch the Junk folder.' },
];

const DNS_RECORDS = [
  { key: 'SPF',   what: 'Tells receivers Resend is allowed to send for forgeledger.net.', where: 'TXT record on forgeledger.net' },
  { key: 'DKIM',  what: "Cryptographic signature proving emails weren't tampered with.", where: 'Three CNAME records (resend._domainkey…)' },
  { key: 'DMARC', what: 'Tells receivers what to do when SPF/DKIM fail. Start with p=none.', where: 'TXT record on _dmarc.forgeledger.net' },
];

const AdminEmailTest = () => {
  const { user } = useAuth();
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminOrAbove(user)) return <Navigate to="/home" replace />;

  const fire = async (overrideTo) => {
    const target = (overrideTo || to).trim();
    if (!target) { toast.error('Enter an email address first.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/email-test/send`,
        { to: target, note: note || null },
        { withCredentials: true },
      );
      if (r.data.ok) {
        toast.success(`Sent to ${target}. Check inbox (and spam folder).`);
      } else {
        toast.error(r.data.error || 'Send failed.');
      }
      setHistory((h) => [{ ...r.data, ts: new Date().toLocaleTimeString('en-GB') }, ...h].slice(0, 10));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-email-test-page">
      <PageTitle
        title="Email deliverability test"
        subtitle="Fire a real transactional email to verify Gmail / Outlook / iCloud delivery."
        className="mb-6"
      />

      {/* Sender + composer */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Mail className="w-5 h-5 text-[#0052FF] mt-1" />
          <div>
            <p className="text-sm font-bold text-[#020617]">Send a test</p>
            <p className="text-xs text-[#64748B]">Uses the same Resend sender + scrubbing as production email flows.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1">Recipient</label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="you@example.com"
              data-testid="email-test-to"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1">Note (optional)</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. iCloud round 2"
              data-testid="email-test-note"
            />
          </div>
        </div>

        <Button
          type="button"
          onClick={() => fire()}
          disabled={busy || !to}
          className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
          data-testid="email-test-send"
        >
          <Send className="w-4 h-4 mr-1.5" />
          {busy ? 'Sending…' : 'Send test email'}
        </Button>
      </div>

      {/* Quick presets */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <p className="text-sm font-bold text-[#020617] mb-1">Provider checklist</p>
        <p className="text-xs text-[#64748B] mb-4">
          Send a test to each mail provider you care about. Goal: inbox on all three.
          If anything lands in spam, work the DNS records below until it doesn't.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PROVIDER_CHECKS.map((p) => (
            <div key={p.name} className="border border-[#E2E8F0] rounded-lg p-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">{p.name}</p>
              <p className="text-[11px] text-[#94A3B8] mb-2">{p.hint}</p>
              <Input
                type="email"
                placeholder={p.placeholder}
                className="mb-2 text-xs"
                id={`provider-${p.name}`}
                data-testid={`provider-input-${p.name.toLowerCase()}`}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full text-xs"
                disabled={busy}
                onClick={() => {
                  const el = document.getElementById(`provider-${p.name}`);
                  if (el?.value) fire(el.value);
                  else toast.error(`Enter a ${p.name} address first.`);
                }}
                data-testid={`provider-send-${p.name.toLowerCase()}`}
              >
                Send to {p.name}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="email-test-history">
          <p className="text-sm font-bold text-[#020617] mb-3">Recent sends (this session)</p>
          <ul className="space-y-2">
            {history.map((h, i) => (
              <li key={i} className="flex items-center gap-2 text-xs border-b border-[#F1F5F9] pb-2 last:border-0">
                {h.ok
                  ? <CheckCircle2 className="w-4 h-4 text-[#047857]" />
                  : <AlertCircle className="w-4 h-4 text-[#DC2626]" />}
                <span className="font-mono text-[#475569]">{h.ts}</span>
                <span className="text-[#020617] font-medium">{h.to}</span>
                {h.ok
                  ? <span className="text-[#047857]">delivered to Resend</span>
                  : <span className="text-[#DC2626]">{h.error || h.code}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* DNS reference */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <p className="text-sm font-bold text-[#020617] mb-1">DNS records to verify</p>
        <p className="text-xs text-[#64748B] mb-4">
          Open <a className="text-[#0052FF] hover:underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains <ExternalLink className="w-3 h-3 inline" /></a>
          {' '}— all three must show a green tick for forgeledger.net before launch.
        </p>
        <ul className="space-y-3">
          {DNS_RECORDS.map((r) => (
            <li key={r.key} className="flex items-start gap-3">
              <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569] flex-shrink-0">{r.key}</span>
              <div>
                <p className="text-sm text-[#020617]">{r.what}</p>
                <p className="text-[11px] text-[#94A3B8]">{r.where}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AdminEmailTest;
